import React, { MutableRefObject } from 'react'

// Components
import Skeleton from 'components/Skeleton'
import Breakpoint from 'components/Breakpoint'
import { TabPanel, Tabs } from 'components/Tabs'
import { pluralize } from 'components/Pluralize'

// Styles
import {
  StyledTabList,
  RoleTabsContainer,
  StyledTab,
  FiltersWrapper,
} from './RoleTabs.styles'

// Types
import {
  GetWorkspaceMembershipsQueryVariables,
  WorkspaceMembershipFragment,
} from 'gqlTypes'

import { MembershipsPanelFilters } from '../../hooks/useMembershipsPanel'

type UserCountType = {
  totalMembers: number | null
  totalGuests: number | null
}

interface RoleTabsProps {
  filters: MembershipsPanelFilters
  userCount: MutableRefObject<UserCountType>
}

// Helpers
const getRoleFromTab = (
  tabIndex: number
): WorkspaceMembershipFragment['role'] | undefined => {
  if (tabIndex === 1) {
    return 'GUEST'
  }

  // an 'undefined' role will reset the filter to all
  return undefined
}

/*
 * Tabs can have 2 options selected at the same time.
 * And for that to work we contain the second set of tabs like so:
 *
 * <TabList>
 *   <Tab>One</Tab>       // can be selected
 *   <div>                // second set of tabs
 *     <Tab>Two-A</Tab>   // can also selected
 *     <Tab>Two-B</Tab>
 *   </div>
 * </TabList>
 *
 * TabList injects a "onTabSelected" prop on its children which is
 * an invalid prop for DOM elements, causing a warning.
 *
 * This component is just a div that forwards its children only
 * and removes all injected props
 */
const TabFilters: React.FC = ({ children }) => {
  return <FiltersWrapper>{children}</FiltersWrapper>
}

/*
 * RoleTabs
 *
 * Renders the filter tabs for the Memberships Table
 */
const RoleTabs: React.FC<RoleTabsProps> = ({ filters, userCount }) => {
  /** Set the Role filter */
  const handleTabsSelected = (selectedTabIndex: number) => {
    const role = getRoleFromTab(selectedTabIndex)
    filters.setRoleFilter(role)
  }

  /** Set the Access level filter */
  const handleAccessLevelClick = (
    accessLevel: GetWorkspaceMembershipsQueryVariables['accessLevel']
  ) => {
    filters.setAccessLevelFilter(accessLevel)
  }

  const renderAccessLevelTabFilters = () => {
    /** Only render Access Level tab filters when "Guests" is NOT the chosen role */
    if (filters.roleFilter !== 'GUEST') {
      return (
        <TabFilters>
          <StyledTab
            // an 'undefined' access level will reset the filter to all
            onTabSelected={() => handleAccessLevelClick(undefined)}
            selected={filters.accessLevelFilter === undefined}>
            <Breakpoint on="md">
              {matches => (matches ? 'View All' : 'All')}
            </Breakpoint>
          </StyledTab>
          <StyledTab
            onTabSelected={() => handleAccessLevelClick('EDITOR')}
            selected={filters.accessLevelFilter === 'EDITOR'}>
            Editors
          </StyledTab>
          <StyledTab
            onTabSelected={() => handleAccessLevelClick('VIEWER')}
            selected={filters.accessLevelFilter === 'VIEWER'}>
            Viewers
          </StyledTab>
        </TabFilters>
      )
    }

    return null
  }

  return (
    <RoleTabsContainer>
      <Tabs
        onTabSelected={handleTabsSelected}
        selectedTabIndex={filters.roleFilter === 'GUEST' ? 1 : 0}>
        <StyledTabList>
          {/** Role tabs*/}
          <StyledTab>
            {userCount?.current?.totalMembers !== null ? (
              `${userCount.current.totalMembers} Total`
            ) : (
              <Skeleton height="10px" width="60px" />
            )}
          </StyledTab>
          <StyledTab>
            {userCount?.current?.totalGuests !== null ? (
              `${userCount.current.totalGuests} ${pluralize(
                'Guest',
                'Guests',
                userCount.current.totalGuests
              )}`
            ) : (
              <Skeleton height="10px" width="60px" />
            )}
          </StyledTab>

          {/** Renders the Access Level tabs*/}
          <>{renderAccessLevelTabFilters()}</>
        </StyledTabList>

        {/* We don't care about the panels. Tabs triggger query filters */}
        <TabPanel>
          <></>
        </TabPanel>
      </Tabs>
    </RoleTabsContainer>
  )
}

export default RoleTabs
